import { useCallback, useEffect, useRef, useState } from 'react';

import { NodeDimensions } from '../types/nodeTypes';

export const useThrottledResize = (delay: number = 16, initialDimensions?: NodeDimensions) => {
  const pendingUpdateRef = useRef<NodeDimensions | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const isMountedRef = useRef(true);
  const [dimensions, setDimensions] = useState<NodeDimensions | null>(initialDimensions || null);

  const scheduleUpdate = useCallback(
    (newDimensions: NodeDimensions) => {
      // Don't schedule updates if component is unmounted
      if (!isMountedRef.current) return;

      pendingUpdateRef.current = newDimensions;

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Schedule update
      timeoutRef.current = setTimeout(() => {
        // Check if component is still mounted before updating state
        if (isMountedRef.current && pendingUpdateRef.current) {
          setDimensions(pendingUpdateRef.current);
          pendingUpdateRef.current = null;
        }
        timeoutRef.current = undefined;
      }, delay);
    },
    [delay]
  );

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      pendingUpdateRef.current = null;
    };
  }, []);

  return { dimensions, scheduleUpdate };
};
